import { useEffect, useRef, useState } from 'react';

// Hook pour détecter si un élément est visible dans le viewport
export const useInView = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let observer = null;
    
    const startObserving = () => {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(element);
          }
        },
        {
          threshold: 0.05,
          // Déclenche quand l'élément est à 8% du bas de l'écran
          rootMargin: '0px 0px -8% 0px',
        }
      );
      observer.observe(element);
    };

    // Délai court pour s'assurer que l'élément est rendu avec opacity: 0
    const timer = setTimeout(startObserving, 80);

    return () => {
      clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, []);

  return [ref, isInView];
};

// Hook pour animations au scroll avec délai
export const useScrollAnimation = (animation = 'fade-up', delay = 0) => {
  const [ref, isInView] = useInView();
  
  const animationClass = isInView 
    ? `scroll-animate ${animation} visible` 
    : `scroll-animate ${animation}`;
  
  const style = delay > 0 ? { transitionDelay: `${delay}ms` } : {};

  return { ref, animationClass, style, isInView };
};

// Composant wrapper pour animations
export const AnimateOnScroll = ({ 
  children, 
  animation = 'fade-up', 
  delay = 0,
  className = '',
  as: Component = 'div',
  ...props 
}) => {
  const { ref, animationClass, style } = useScrollAnimation(animation, delay);

  return (
    <Component
      ref={ref}
      className={`${animationClass} ${className}`}
      style={style}
      {...props}
    >
      {children}
    </Component>
  );
};

export default useScrollAnimation;
